import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/auth';

interface UserData {
    id: string;
    name: string;
    email: string;
    phone?: string;
    password?: string;
    birthDate?: string;
    createdAt?: string;
}

const DadosPessoais: React.FC = () => {
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<UserData>>({});
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Helper function to convert ISO date to YYYY-MM-DD format
    const formatDateForInput = (isoDate: string | undefined): string => {
        if (!isoDate) return '';
        return isoDate.split('T')[0];
    };

    // Helper function to calculate age
    const calculateAge = (birthDate: string): number => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    // Get max date for 18 years old
    const getMaxBirthDate = (): string => {
        const today = new Date();
        const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
        return maxDate.toISOString().split('T')[0];
    };

    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async () => {
        try {
            const response = await fetchWithAuth('/api/users/me', { method: 'GET' });
            
            if (!response.ok) {
                if (response.status === 401) {
                    setMessage({ type: 'error', text: 'Sua sessão expirou. Por favor, faça login novamente.' });
                } else {
                    setMessage({ type: 'error', text: 'Erro ao carregar dados do perfil' });
                }
                return;
            }
            
            const data = await response.json();
            setUserData(data);
            // Format birthDate for the date input
            setFormData({
                ...data,
                birthDate: formatDateForInput(data.birthDate)
            });
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            setMessage({ type: 'error', text: 'Erro ao carregar dados do perfil' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate age if birthDate is provided
        if (formData.birthDate) {
            const age = calculateAge(formData.birthDate);
            if (age < 18) {
                setMessage({ type: 'error', text: 'Deve ter pelo menos 18 anos de idade.' });
                return;
            }
        }
        
        try {
            const response = await fetchWithAuth('/api/users/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                const updatedData = await response.json();
                setUserData(updatedData);
                setIsEditing(false);
                setMessage({ type: 'success', text: 'Dados atualizados com sucesso!' });
            }
        } catch (error) {
            console.error('Erro ao atualizar dados:', error);
            setMessage({ type: 'error', text: 'Erro ao atualizar dados' });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    if (loading) {
        return <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>;
    }

    return (
        <div className="">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[conic-gradient(from_220deg,#e2b714,#e86fde,#4da9ff,#e2b714)] p-[2px]">
                        <div className="w-full h-full rounded-full bg-[#05060a] flex items-center justify-center text-sm font-bold tracking-wide text-white">
                            {(userData?.name?.charAt(0) || 'U').toUpperCase()}
                        </div>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white">Dados Pessoais</h2>
                        <p className="text-xs text-[#94a3b8]">Atualize as suas informações de conta</p>
                    </div>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 rounded-full bg-gradient-to-r from-[#8b5cf6] to-[#ec4899] text-white font-medium hover:brightness-110 transition"
                    >
                        Editar
                    </button>
                )}
            </div>

            {message && (
                <div
                    className={`mb-4 p-4 rounded-lg border text-sm ${message.type === 'success'
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                            : 'bg-rose-500/10 border-rose-500/30 text-rose-200'
                        }`}
                >
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-white">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#cbd5e1] mb-1">Nome Completo</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] placeholder-[#64748b] focus:ring-2 focus:ring-[#8b5cf6] focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#cbd5e1] mb-1">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ''}
                            disabled
                            className="w-full px-4 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] opacity-60 cursor-not-allowed"
                        />
                        <p className="text-xs text-[#94a3b8] mt-1">O email não pode ser alterado</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#cbd5e1] mb-1">Telefone</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] placeholder-[#64748b] focus:ring-2 focus:ring-[#8b5cf6] focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                            placeholder="+351 XXX XXX XXX"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#cbd5e1] mb-1">Data de Nascimento</label>
                        <input
                            type="date"
                            name="birthDate"
                            value={formData.birthDate || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            max={getMaxBirthDate()}
                            className="w-full px-4 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] placeholder-[#64748b] focus:ring-2 focus:ring-[#8b5cf6] focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                        {isEditing && <p className="text-xs text-[#94a3b8] mt-1">Deve ter pelo menos 18 anos</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#cbd5e1] mb-1">Password (preencha caso queira alterar)</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] placeholder-[#64748b] focus:ring-2 focus:ring-[#8b5cf6] focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                    </div>

                    {userData?.createdAt && (
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-[#cbd5e1] mb-1">Membro desde</label>
                            <input
                                type="text"
                                value={new Date(userData.createdAt).toLocaleDateString('pt-PT')}
                                disabled
                                className="w-full px-4 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] opacity-60 cursor-not-allowed"
                            />
                        </div>
                    )}
                </div>

                {isEditing && (
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        <button
                            type="submit"
                            className="sm:flex-1 px-4 py-2 rounded-full bg-gradient-to-r from-[#8b5cf6] to-[#ec4899] text-white font-medium hover:brightness-110 transition"
                        >
                            Guardar
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setIsEditing(false);
                                setFormData({
                                    ...userData,
                                    birthDate: formatDateForInput(userData?.birthDate)
                                });
                            }}
                            className="sm:flex-1 px-4 py-2 rounded-full bg-white/5 text-[#e5e7eb] border border-[#1f2937] hover:bg-white/10 transition"
                        >
                            Cancelar
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default DadosPessoais;
